import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import useRecorder from 'shared/useRecorder'
import useMicLevels, { drawIdleWave, drawTakeWave } from 'shared/useMicLevels'
import MicBlockedNotice from 'shared/MicBlockedNotice'
import { useTranslation } from 'react-i18next'
import { formatClock } from './logic'

export interface Take {
  file: File
  url: string
  mime: string
  seconds: number
  /** The live waveform's levels, frozen into the take and used as the scrubber. */
  bars: number[]
}

export type StopReason = 'tap' | 'silence' | 'cap'

export interface StepRecorderHandle {
  /** Start a fresh recording (used by "Record again" in the script). */
  start: () => void
  stop: () => void
}

interface StepRecorderProps {
  take: Take | null
  /** Label for the take, e.g. "Reading a passage". */
  takeLabel: string
  nudge: boolean
  disabled?: boolean
  onStarted: (stream: MediaStream, startedAt: number) => void
  onStopped: (reason: StopReason, seconds: number) => void
  onTake: (take: Take) => void
  onDiscard: () => void
}

const MAX_RECORDING_MS = 5 * 60 * 1000
const SILENCE_AUTO_STOP_MS = 20 * 1000

const MicIcon: React.FC = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <rect x='9' y='3' width='6' height='11' rx='3' />
    <path d='M5 11a7 7 0 0 0 14 0M12 18v3' />
  </svg>
)

/**
 * The step's recorder: a big mic, a live waveform while recording, and the take
 * frozen into its own waveform, which fills green as it plays and can be tapped
 * or dragged to seek. Stops itself after 20 s of silence or 5 minutes.
 */
const StepRecorder = forwardRef<StepRecorderHandle, StepRecorderProps>(
  ({ take, takeLabel, nudge, disabled, onStarted, onStopped, onTake, onDiscard }, ref) => {
    const { t } = useTranslation('writeSpeak')
    const { audioURL, isRecording, startRecording, stopRecording, mediaRecorder, error } = useRecorder()
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const startedAtRef = useRef(0)
    const elapsedRef = useRef(0)
    const expectTakeRef = useRef(false)
    const [elapsed, setElapsed] = useState(0)
    const [playing, setPlaying] = useState(false)
    const [progress, setProgress] = useState(0)
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const scrubbingRef = useRef(false)

    const live = isRecording && !!mediaRecorder
    const busy = isRecording // includes the moment the microphone is still opening

    const stop = (reason: StopReason) => {
      if (!isRecording) return
      stopRecording(false)
      onStopped(reason, elapsedRef.current)
    }
    const stopRef = useRef(stop)
    stopRef.current = stop

    const levelsRef = useMicLevels({
      stream: mediaRecorder?.stream,
      active: live,
      canvasRef,
      silenceMs: SILENCE_AUTO_STOP_MS,
      onSilence: () => stopRef.current('silence'),
    })

    const start = () => {
      if (disabled) return
      expectTakeRef.current = true
      setElapsed(0)
      elapsedRef.current = 0
      startRecording()
    }

    useImperativeHandle(ref, () => ({ start, stop: () => stopRef.current('tap') }))

    // The microphone is open: the recording has really started.
    useEffect(() => {
      if (!live || !mediaRecorder) return
      startedAtRef.current = performance.now()
      onStarted(mediaRecorder.stream, startedAtRef.current)
      const tick = setInterval(() => {
        elapsedRef.current = (performance.now() - startedAtRef.current) / 1000
        setElapsed(elapsedRef.current)
      }, 250)
      const cap = setTimeout(() => stopRef.current('cap'), MAX_RECORDING_MS)
      return () => {
        clearInterval(tick)
        clearTimeout(cap)
        elapsedRef.current = (performance.now() - startedAtRef.current) / 1000
      }
    }, [live, mediaRecorder])

    // MediaRecorder hands over the file after stop.
    useEffect(() => {
      if (!audioURL.data || !expectTakeRef.current) return
      expectTakeRef.current = false
      onTake({
        file: audioURL.file as File,
        url: audioURL.data,
        mime: audioURL.type,
        seconds: Math.max(1, Math.round(elapsedRef.current)),
        bars: levelsRef.current.slice(),
      })
    }, [audioURL])

    // Playback element for the take.
    useEffect(() => {
      setPlaying(false)
      setProgress(0)
      if (!take) {
        audioRef.current = null
        return
      }
      const el = new Audio(take.url)
      el.preload = 'auto'
      const onEnded = () => {
        setPlaying(false)
        setProgress(1)
      }
      el.addEventListener('ended', onEnded)
      audioRef.current = el
      return () => {
        el.pause()
        el.removeEventListener('ended', onEnded)
      }
    }, [take])

    useEffect(() => {
      if (!playing || !take) return
      let raf = 0
      const loop = () => {
        const el = audioRef.current
        if (el && !scrubbingRef.current) setProgress(Math.min(1, el.currentTime / take.seconds))
        raf = requestAnimationFrame(loop)
      }
      raf = requestAnimationFrame(loop)
      return () => cancelAnimationFrame(raf)
    }, [playing, take])

    // Idle baseline or frozen take; the live waveform is drawn by useMicLevels.
    useEffect(() => {
      if (busy) return
      const draw = () => {
        if (take) drawTakeWave(canvasRef.current, take.bars, progress)
        else drawIdleWave(canvasRef.current)
      }
      draw()
      const canvas = canvasRef.current
      if (!canvas || typeof ResizeObserver === 'undefined') return
      const ro = new ResizeObserver(draw)
      ro.observe(canvas)
      return () => ro.disconnect()
    }, [busy, take, progress])

    const onMic = () => {
      if (isRecording) stop('tap')
      else start()
    }

    const togglePlay = () => {
      const el = audioRef.current
      if (!el || !take) return
      if (playing) {
        el.pause()
        setPlaying(false)
        return
      }
      if (progress >= 1) {
        try {
          el.currentTime = 0
        } catch (e) {}
        setProgress(0)
      }
      el.play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false))
    }

    const seek = (clientX: number) => {
      const canvas = canvasRef.current
      const el = audioRef.current
      if (!canvas || !el || !take) return
      const rect = canvas.getBoundingClientRect()
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
      try {
        el.currentTime = ratio * take.seconds
      } catch (e) {}
      setProgress(ratio)
    }

    const onScrubKey = (e: React.KeyboardEvent) => {
      if (!take) return
      const delta = e.key === 'ArrowRight' ? 2 : e.key === 'ArrowLeft' ? -2 : 0
      if (!delta) return
      e.preventDefault()
      const next = Math.min(1, Math.max(0, progress + delta / take.seconds))
      try {
        if (audioRef.current) audioRef.current.currentTime = next * take.seconds
      } catch (err) {}
      setProgress(next)
    }

    const discard = () => {
      audioRef.current?.pause()
      setPlaying(false)
      onDiscard()
    }

    if (error && !take && !busy) {
      return (
        <div className='ws-rec-blocked'>
          <MicBlockedNotice error={error} onRetry={start} />
        </div>
      )
    }

    const status = busy ? t('recorder.statusRecording') : take ? takeLabel : t('recorder.statusIdle')
    const time = busy
      ? formatClock(elapsed)
      : take
        ? playing || (progress > 0 && progress < 1)
          ? `${formatClock(progress * take.seconds)} / ${formatClock(take.seconds)}`
          : formatClock(take.seconds)
        : '0:00'

    return (
      <div
        className={`ws-rec${busy ? ' recording' : ''}${nudge && !busy && !take ? ' nudge' : ''}${take ? ' has-take' : ''}`}
      >
        {!take && (
          <div className='ws-mic-wrap'>
            <button
              type='button'
              className='ws-mic'
              onClick={onMic}
              disabled={disabled}
              aria-label={busy ? t('recorder.stop') : t('recorder.start')}
            >
              <MicIcon />
              <span className='ws-stop' aria-hidden='true' />
            </button>
          </div>
        )}
        <div className='ws-rec-mid'>
          <div className='ws-rec-status' aria-live='polite'>
            <span>{status}</span>
            <span className='t'>{time}</span>
          </div>
          <canvas
            ref={canvasRef}
            className={`ws-wave${take && !busy ? ' is-seekable' : ''}`}
            role={take && !busy ? 'slider' : undefined}
            tabIndex={take && !busy ? 0 : -1}
            aria-label={take && !busy ? t('recorder.position') : undefined}
            aria-hidden={take && !busy ? undefined : true}
            aria-valuemin={take && !busy ? 0 : undefined}
            aria-valuemax={take && !busy ? take.seconds : undefined}
            aria-valuenow={take && !busy ? Math.round(progress * take.seconds) : undefined}
            aria-valuetext={take && !busy ? formatClock(progress * take.seconds) : undefined}
            onPointerDown={(e) => {
              if (!take || busy) return
              scrubbingRef.current = true
              try {
                e.currentTarget.setPointerCapture(e.pointerId)
              } catch (err) {}
              seek(e.clientX)
            }}
            onPointerMove={(e) => scrubbingRef.current && seek(e.clientX)}
            onPointerUp={() => (scrubbingRef.current = false)}
            onPointerCancel={() => (scrubbingRef.current = false)}
            onKeyDown={onScrubKey}
          />
          {take && !busy && (
            <div className='ws-rec-actions'>
              <button
                type='button'
                className='ws-play'
                onClick={togglePlay}
                aria-label={playing ? t('recorder.pauseYours') : t('recorder.playYours')}
              >
                <svg viewBox='0 0 16 16' aria-hidden='true'>
                  {playing ? <path d='M4 2h3v12H4zM9 2h3v12H9z' /> : <path d='M4 2l10 6-10 6z' />}
                </svg>
              </button>
              <button type='button' className='ws-textbtn' onClick={discard} disabled={disabled}>
                {t('recorder.recordAgain')}
              </button>
            </div>
          )}
        </div>
      </div>
    )
  },
)

export default StepRecorder
